import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import realtimePaymentAlertService from '../../../services/realtimePaymentAlertService';

const RealtimePaymentAlerts = ({ onNewPayment }) => {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    // Check browser notification permission
    if ('Notification' in window && Notification.permission === 'granted') {
      setBrowserNotificationsEnabled(true);
    }

    // Load initial pending count
    loadPendingCount();

    // Subscribe to real-time payment changes
    const channel = realtimePaymentAlertService?.subscribeToPaymentTransactions(
      (payment) => handleNewPayment(payment),
      (newPayment, oldPayment) => handlePaymentUpdate(newPayment, oldPayment)
    );

    setIsConnected(true);

    // Cleanup subscription on unmount
    return () => {
      realtimePaymentAlertService?.unsubscribeFromPaymentTransactions(channel);
      setIsConnected(false);
    };
  }, []);

  const loadPendingCount = async () => {
    const count = await realtimePaymentAlertService?.getPendingPaymentCount();
    setPendingCount(count);
  };

  const handleNewPayment = (payment) => {
    const notification = realtimePaymentAlertService?.formatPaymentNotification(payment);
    
    // Add to notifications list
    setNotifications(prev => [notification, ...prev]?.slice(0, 10)); // Keep last 10

    // Update pending count
    setPendingCount(prev => prev + 1);

    // Play sound if enabled
    if (soundEnabled) {
      realtimePaymentAlertService?.playNotificationSound();
    }

    // Show browser notification if enabled
    if (browserNotificationsEnabled) {
      realtimePaymentAlertService?.showBrowserNotification(payment);
    }

    // Notify parent component
    if (onNewPayment) {
      onNewPayment(payment);
    }
  };

  const handlePaymentUpdate = (newPayment, oldPayment) => {
    // If payment was reconciled, decrease pending count
    if (oldPayment?.reconciliation_status === 'pending' && newPayment?.reconciliation_status === 'reconciled') {
      setPendingCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleEnableBrowserNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setBrowserNotificationsEnabled(permission === 'granted');
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const getBankBadgeColor = (method) => {
    const colors = {
      bsp: 'bg-blue-100 text-blue-700',
      kinabank: 'bg-green-100 text-green-700',
      creditbank: 'bg-purple-100 text-purple-700'
    };
    return colors?.[method] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
            }`} />
            <h3 className="text-sm font-semibold text-foreground">
              Real-Time Payment Alerts
            </h3>
          </div>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 bg-error text-white text-xs font-bold rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1.5 rounded transition-colors ${
              soundEnabled ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground'
            }`}
            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
          >
            <Icon name={soundEnabled ? 'Volume2' : 'VolumeX'} size={16} />
          </button>
          {!browserNotificationsEnabled && (
            <button
              onClick={handleEnableBrowserNotifications}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Enable browser notifications"
            >
              <Icon name="Bell" size={16} />
            </button>
          )}
          {notifications?.length > 0 && (
            <button
              onClick={clearNotifications}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Clear notifications"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
      </div>
      {/* Connection Status */}
      <div className="px-4 py-2 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-2 text-xs">
          {isConnected ? (
            <>
              <Icon name="Wifi" size={14} className="text-green-500" />
              <span className="text-green-600 font-medium">Connected to real-time updates</span>
            </>
          ) : (
            <>
              <Icon name="WifiOff" size={14} className="text-red-500" />
              <span className="text-red-600 font-medium">Disconnected</span>
            </>
          )}
        </div>
      </div>
      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {notifications?.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Icon name="Bell" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No new payment alerts</p>
            <p className="text-xs text-muted-foreground mt-1">
              You'll be notified instantly when bank transfers are detected
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications?.map((notification) => (
              <div
                key={notification?.id}
                className="px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="CreditCard" size={16} className="text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-foreground">
                        {notification?.title}
                      </p>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                        getBankBadgeColor(notification?.data?.payment_method)
                      }`}>
                        {notification?.data?.payment_method?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {notification?.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {notification?.timestamp?.toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded">
                      NEW
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      {notifications?.length > 0 && (
        <div className="px-4 py-3 border-t border-border bg-muted/30">
          <p className="text-xs text-muted-foreground text-center">
            Showing last {notifications?.length} notification{notifications?.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default RealtimePaymentAlerts;